import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchLockoutSchedule,
  fetchLockoutStatus,
  requestDeskPlanLockout,
  requestLockoutSchedule,
  requestLockoutToggle,
} from "./lockout-api";
import { emptyLockoutState, type LockoutState } from "./lockout-types";
import { useLockoutBlocker } from "./use-lockout-blocker";

export type BriefingAnchor = "mdb" | "adb" | "pmdb";
export type { LockoutState } from "./lockout-types";

const POLICY_EVENT = "fintheon:lockout-policy-updated";

export function useLockout(pollMs = 5000) {
  const [state, setState] = useState<LockoutState>(() => emptyLockoutState());
  const [loading, setLoading] = useState(true);
  const previousLockedRef = useRef(false);
  const notifiedRef = useRef(false);
  const {
    ensureSelectedPlatformBlocked,
    hasSystemBlockerGate,
    releaseSelectedPlatformBlocker,
    requestSystemPermission,
  } = useLockoutBlocker();

  const refresh = useCallback(async () => {
    const next = await fetchLockoutStatus();
    const isAutomatic = next.scheduledBy !== "manual";
    const canApplyAutomatic =
      next.policyResolved && next.policy?.lockoutEnabled === true;

    if (!next.locked || (isAutomatic && !canApplyAutomatic))
      await releaseSelectedPlatformBlocker();

    setState(next);
    setLoading(false);
    if (
      next.locked &&
      (!isAutomatic || canApplyAutomatic) &&
      !previousLockedRef.current &&
      !notifiedRef.current
    ) {
      notifiedRef.current = true;
      fireOsNotification();
    }
    if (!next.locked) notifiedRef.current = false;
    previousLockedRef.current = next.locked;
  }, [releaseSelectedPlatformBlocker]);

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => void refresh(), pollMs);
    const handlePolicyUpdate = () => void refresh();
    window.addEventListener(POLICY_EVENT, handlePolicyUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener(POLICY_EVENT, handlePolicyUpdate);
    };
  }, [pollMs, refresh]);

  const applyLockResult = useCallback(
    async (result: LockoutState, isAutomatic: boolean) => {
      const verified = await fetchLockoutStatus();
      const canApplyAutomatic =
        !isAutomatic ||
        (verified.policyResolved && verified.policy?.lockoutEnabled === true);
      if (result.commandOk && verified.locked && canApplyAutomatic) {
        const preferSystemLayer = await hasSystemBlockerGate();
        await ensureSelectedPlatformBlocked({ preferSystemLayer });
      }
      await refresh();
      return {
        ...verified,
        schedule: result.schedule ?? verified.schedule,
        commandOk: result.commandOk,
        error: result.error,
      };
    },
    [ensureSelectedPlatformBlocked, hasSystemBlockerGate, refresh],
  );

  const lock = useCallback(
    async (durationMinutes?: number, windowStartTime?: string) => {
      const result = windowStartTime
        ? await requestLockoutSchedule({ windowStartTime })
        : await requestLockoutToggle({ locked: true, durationMinutes });
      await applyLockResult(result, !!windowStartTime);
      return result.commandOk === true;
    },
    [applyLockResult],
  );

  const unlock = useCallback(async () => {
    const result = await requestLockoutToggle({ locked: false });
    if (result.commandOk) await releaseSelectedPlatformBlocker();
    await refresh();
    return result.commandOk === true;
  }, [refresh, releaseSelectedPlatformBlocker]);

  const lockUntilBriefing = useCallback(
    async (briefingAnchor: BriefingAnchor) => {
      const result = await requestLockoutToggle({
        locked: true,
        briefingAnchor,
      });
      return await applyLockResult(result, true);
    },
    [applyLockResult],
  );

  const scheduleLock = useCallback(
    async (durationMinutes: number, windowStartTime?: string) => {
      const result = await requestLockoutSchedule({
        durationMinutes: windowStartTime ? undefined : durationMinutes,
        windowStartTime,
      });
      await applyLockResult(result, !!windowStartTime);
      return result.commandOk === true;
    },
    [applyLockResult],
  );

  const lockUntilManual = useCallback(
    async (isoTimestamp: string) => {
      const result = await requestLockoutSchedule({ lockUntil: isoTimestamp });
      await applyLockResult(result, false);
      return result.commandOk === true;
    },
    [applyLockResult],
  );

  const lockUntilDeskSession = useCallback(async () => {
    const result = await requestDeskPlanLockout();
    return await applyLockResult(result, true);
  }, [applyLockResult]);

  const lockUntil = useCallback(
    async (_clientTimestamp: string) => {
      const result = await lockUntilDeskSession();
      return result.commandOk === true;
    },
    [lockUntilDeskSession],
  );

  const getNextWindow = useCallback(async () => {
    return await fetchLockoutSchedule();
  }, []);

  return {
    state,
    loading,
    lock,
    unlock,
    refresh,
    scheduleLock,
    lockUntil,
    lockUntilManual,
    lockUntilBriefing,
    lockUntilDeskSession,
    getNextWindow,
    requestPermission: requestSystemPermission,
  };
}

export function notifyLockoutPolicyUpdated(): void {
  window.dispatchEvent(new CustomEvent(POLICY_EVENT));
}

function fireOsNotification(): void {
  try {
    const runtime = window as unknown as {
      electron?: {
        systemNotification?: {
          show?: (title: string, message: string) => void;
        };
      };
    };
    runtime.electron?.systemNotification?.show?.(
      "Trading locked",
      "Fintheon will release the selected platform at the scheduled time.",
    );
  } catch {
    // Best effort only in supported Electron sessions.
  }
}
